import { useEffect, useMemo, useRef, useState } from "react";
import { Folder, Grip, Pencil, Trash2, X } from "lucide-react";

/* PhoneHome — paged CivicOS launcher with persistent folders.
   Layout shape: pages contain app ids or "folder:<id>"; profile.home_folders maps
   folder id → {id,name,apps}. Old page-only layouts normalize without migration UI. */

export const DEFAULT_HOME_PAGES = [
  ["messages", "calls", "buzzer", "market", "bank", "cityhall", "sos", "settings"],
  ["store", "mail", "notes", "civic", "dmv", "atlas", "skyline", "radio"],
  ["pawn", "lift", "pulse", "medicard", "ghostpak", "greenpages", "ghostwire", "growtrack"],
];

const PAGE_SIZE = 8;
const MAX_PAGES = 4;
const MAX_FOLDER_APPS = 12;

function AppIcon({ app, edit, selected, onPress, onDragStart, onDropApp }) {
  const { id, label, Icon, color, badge } = app;
  return (
    <button
      data-testid={`app-${id}`}
      onClick={() => onPress(id)}
      draggable={edit}
      onDragStart={() => onDragStart(id)}
      onDragOver={(e) => edit && e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); onDropApp(id); }}
      className={`flex flex-col items-center gap-1.5 group relative ${edit ? "cursor-grab active:cursor-grabbing" : ""}`}
    >
      <span className={`relative w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-md border flex items-center justify-center transition-transform group-hover:scale-105 group-active:scale-95 shadow-lg ${edit ? "animate-pulse" : ""} ${selected ? "border-white scale-110" : "border-white/15"}`}
        style={selected ? { boxShadow: `0 0 0 2px ${color}88, 0 12px 28px ${color}33` } : {}}>
        <Icon className="w-6 h-6" style={{ color }} />
        {badge > 0 && <span data-testid={`badge-${id}`} className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white font-mono text-[10px] font-bold flex items-center justify-center">{badge}</span>}
        {edit && <Grip className="absolute -bottom-1 -right-1 w-3.5 h-3.5 text-white/60" />}
      </span>
      <span className="font-mono text-[8.5px] uppercase tracking-[0.15em] text-white/80">{label}</span>
    </button>
  );
}

function FolderSlot({ folder, appsById, edit, selected, accent, onPress, onDropApp, onDragStart }) {
  const preview = folder.apps.slice(0, 4).map((id) => appsById[id]).filter(Boolean);
  return (
    <button data-testid={`folder-${folder.id}`} onClick={() => onPress(folder)} draggable={edit}
            onDragStart={() => onDragStart(`folder:${folder.id}`)}
            onDragOver={(e) => edit && e.preventDefault()} onDrop={(e) => { e.preventDefault(); onDropApp(folder.id); }}
            className={`flex flex-col items-center gap-1.5 group ${edit ? "cursor-pointer" : ""}`}>
      <span className={`w-14 h-14 rounded-2xl border bg-white/10 backdrop-blur-md grid grid-cols-2 place-items-center p-2 transition-transform group-hover:scale-105 ${selected ? "border-white scale-110" : "border-white/15"}`}
        style={selected ? { boxShadow: `0 0 0 2px ${accent}88` } : {}}>
        {preview.map((app) => <app.Icon key={app.id} className="w-4 h-4" style={{ color: app.color }} />)}
        {!preview.length && <Folder className="w-6 h-6" style={{ color: accent }} />}
      </span>
      <span className="font-mono text-[8.5px] uppercase tracking-[0.15em] text-white/80 max-w-16 truncate">{folder.name}</span>
    </button>
  );
}

export default function PhoneHome({ apps, dock, profile, me, now, accent, onSaveLayout }) {
  const [page, setPage] = useState(0);
  const [edit, setEdit] = useState(false);
  const [selected, setSelected] = useState("");
  const [notice, setNotice] = useState("");
  const [openFolderId, setOpenFolderId] = useState("");
  const [folderName, setFolderName] = useState("");
  const [localPages, setLocalPages] = useState([]);
  const [localFolders, setLocalFolders] = useState({});
  const swipeStart = useRef(null);
  const byId = useMemo(() => Object.fromEntries(apps.map((a) => [a.id, a])), [apps]);

  const normalized = useMemo(() => {
    const visibleApps = new Set(apps.map((a) => a.id));
    const sourceFolders = profile.home_folders || {};
    const folders = {};
    const folderAppSet = new Set();
    Object.values(sourceFolders).forEach((f) => {
      const cleanApps = (f.apps || []).filter((id) => visibleApps.has(id)).slice(0, MAX_FOLDER_APPS);
      if (f.id && cleanApps.length >= 2) {
        folders[f.id] = { id: f.id, name: f.name || "Folder", apps: cleanApps };
        cleanApps.forEach((id) => folderAppSet.add(id));
      }
    });
    const saved = Array.isArray(profile.home_layout) && profile.home_layout.length ? profile.home_layout : DEFAULT_HOME_PAGES;
    const pages = saved.map((p) => (Array.isArray(p) ? p : []).filter((item) => {
      if (item.startsWith("folder:")) return Boolean(folders[item.slice(7)]);
      return visibleApps.has(item) && !folderAppSet.has(item);
    }).slice(0, PAGE_SIZE));
    while (pages.length && !pages[pages.length - 1].length) pages.pop();
    if (!pages.length) pages.push([]);
    const placed = new Set(pages.flat());
    Object.values(folders).forEach((folder) => {
      const item = `folder:${folder.id}`;
      if (placed.has(item)) return;
      let target = pages.find((p) => p.length < PAGE_SIZE);
      if (!target && pages.length < MAX_PAGES) { pages.push([]); target = pages[pages.length - 1]; }
      if (target) { target.push(item); placed.add(item); }
    });
    for (const app of apps) {
      if (placed.has(app.id) || folderAppSet.has(app.id)) continue;
      let target = pages.find((p) => p.length < PAGE_SIZE);
      if (!target && pages.length < MAX_PAGES) { pages.push([]); target = pages[pages.length - 1]; }
      if (target) { target.push(app.id); placed.add(app.id); }
    }
    return { pages: pages.filter((p) => p.length), folders };
  }, [apps, profile.home_folders, profile.home_layout]);

  useEffect(() => {
    setLocalPages(normalized.pages);
    setLocalFolders(normalized.folders);
  }, [normalized]);
  useEffect(() => { if (page >= localPages.length) setPage(Math.max(0, localPages.length - 1)); }, [page, localPages.length]);

  const showNotice = (text) => {
    setNotice(text);
    window.setTimeout(() => setNotice(""), 2200);
  };

  const commit = (pages, folders) => {
    const cleanPages = pages.map((p) => p.filter(Boolean).slice(0, PAGE_SIZE)).filter((p) => p.length);
    const orphans = [];
    const cleanFolders = {};
    Object.values(folders).forEach((f) => {
      if (f.apps.length >= 2) cleanFolders[f.id] = { id: f.id, name: f.name || "Folder", apps: f.apps };
      else orphans.push(...f.apps);
    });
    cleanPages.forEach((p, i) => { cleanPages[i] = p.filter((item) => !item.startsWith("folder:") || cleanFolders[item.slice(7)]); });
    orphans.forEach((id) => {
      let target = cleanPages.find((p) => p.length < PAGE_SIZE);
      if (!target && cleanPages.length < MAX_PAGES) { cleanPages.push([]); target = cleanPages[cleanPages.length - 1]; }
      if (target) target.push(id);
    });
    const nonEmptyPages = cleanPages.filter((p) => p.length);
    setLocalPages(nonEmptyPages);
    setLocalFolders(cleanFolders);
    onSaveLayout(nonEmptyPages, cleanFolders);
  };

  const clone = () => [localPages.map((p) => [...p]), Object.fromEntries(Object.entries(localFolders).map(([k, f]) => [k, { ...f, apps: [...f.apps] }]))];

  const locate = (id) => {
    for (let p = 0; p < localPages.length; p += 1) {
      const i = localPages[p].indexOf(id);
      if (i >= 0) return { type: "page", p, i };
    }
    for (const [folderId, folder] of Object.entries(localFolders)) {
      const i = folder.apps.indexOf(id);
      if (i >= 0) return { type: "folder", folderId, i };
    }
    return null;
  };

  const removeItem = (pages, folders, id) => {
    for (let p = 0; p < pages.length; p += 1) {
      const i = pages[p].indexOf(id);
      if (i >= 0) return pages[p].splice(i, 1)[0];
    }
    for (const folder of Object.values(folders)) {
      const i = folder.apps.indexOf(id);
      if (i >= 0) folder.apps.splice(i, 1);
    }
    return id;
  };

  const createFolder = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId || sourceId.startsWith("folder:") || targetId.startsWith("folder:")) return;
    const targetLoc = locate(targetId);
    if (!targetLoc || targetLoc.type !== "page") return;
    const [pages, folders] = clone();
    const id = `fld-${Date.now().toString(36)}`;
    removeItem(pages, folders, sourceId);
    removeItem(pages, folders, targetId);
    pages[targetLoc.p].splice(Math.min(targetLoc.i, pages[targetLoc.p].length), 0, `folder:${id}`);
    folders[id] = { id, name: "Folder", apps: [sourceId, targetId] };
    setSelected("");
    commit(pages, folders);
    setOpenFolderId(id);
    setFolderName("Folder");
  };

  const addToFolder = (folderId, appId = selected) => {
    if (!appId || appId.startsWith("folder:")) return;
    const folder = localFolders[folderId];
    if (!folder) return;
    if (folder.apps.length >= MAX_FOLDER_APPS) return showNotice("Folder is full");
    const [pages, folders] = clone();
    removeItem(pages, folders, appId);
    folders[folderId].apps.push(appId);
    setSelected("");
    commit(pages, folders);
  };

  const moveToPage = (targetPage) => {
    if (!selected) { setPage(targetPage); return; }
    const [pages, folders] = clone();
    const loc = locate(selected);
    if (!loc) return;
    if (loc.type === "page" && loc.p === targetPage) { setSelected(""); setPage(targetPage); return; }
    if (pages[targetPage].length >= PAGE_SIZE) return showNotice("That page is full — swap with an app instead");
    removeItem(pages, folders, selected);
    pages[targetPage].push(selected);
    setSelected("");
    setOpenFolderId("");
    setPage(targetPage);
    commit(pages, folders);
  };

  const pressIcon = (id) => {
    if (!edit) { byId[id]?.onOpen?.(); return; }
    if (!selected) { setSelected(id); return; }
    if (selected === id) { setSelected(""); return; }
    createFolder(selected, id);
  };

  const pressFolder = (folder) => {
    if (edit && selected && !selected.startsWith("folder:")) { addToFolder(folder.id); return; }
    setOpenFolderId(folder.id);
    setFolderName(folder.name);
  };

  const folderAppPress = (folder, appId) => {
    if (!edit) { byId[appId]?.onOpen?.(); return; }
    if (!selected) { setSelected(appId); return; }
    if (selected === appId) { setSelected(""); return; }
    const [pages, folders] = clone();
    const appsList = folders[folder.id].apps;
    const a = appsList.indexOf(selected);
    const b = appsList.indexOf(appId);
    if (a >= 0 && b >= 0) [appsList[a], appsList[b]] = [appsList[b], appsList[a]];
    setSelected("");
    commit(pages, folders);
  };

  const moveSelectedOut = () => {
    const folder = localFolders[openFolderId];
    if (!folder || !selected || !folder.apps.includes(selected)) return;
    if (localPages[page].length >= PAGE_SIZE) return showNotice("This page is full — move it to another page first");
    const [pages, folders] = clone();
    folders[openFolderId].apps = folders[openFolderId].apps.filter((id) => id !== selected);
    pages[page].push(selected);
    setSelected("");
    commit(pages, folders);
  };

  const dissolveFolder = () => {
    const folder = localFolders[openFolderId];
    if (!folder) return;
    const freeSlots = (PAGE_SIZE - localPages[page].length) + (MAX_PAGES - localPages.length) * PAGE_SIZE;
    if (folder.apps.length > freeSlots) return showNotice("Not enough room to dissolve this folder");
    const [pages, folders] = clone();
    delete folders[openFolderId];
    for (const p of pages) {
      const i = p.indexOf(`folder:${openFolderId}`);
      if (i >= 0) p.splice(i, 1);
    }
    const room = Math.max(0, PAGE_SIZE - pages[page].length);
    pages[page].push(...folder.apps.slice(0, room));
    let remaining = folder.apps.slice(room);
    while (remaining.length) {
      if (pages.length >= MAX_PAGES) break;
      pages.push(remaining.slice(0, PAGE_SIZE));
      remaining = remaining.slice(PAGE_SIZE);
    }
    setOpenFolderId("");
    setSelected("");
    commit(pages, folders);
  };

  const renameFolder = () => {
    const name = folderName.trim().slice(0, 18) || "Folder";
    const [pages, folders] = clone();
    if (folders[openFolderId]) folders[openFolderId].name = name;
    commit(pages, folders);
  };

  const pointerDown = (e) => {
    if (edit || openFolderId) return;
    swipeStart.current = { x: e.clientX, y: e.clientY };
  };
  const pointerUp = (e) => {
    const s = swipeStart.current;
    swipeStart.current = null;
    if (!s || edit || openFolderId) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (Math.abs(dx) < 55 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    if (dx < 0) setPage((p) => Math.min(localPages.length - 1, p + 1));
    else setPage((p) => Math.max(0, p - 1));
  };

  const openFolder = localFolders[openFolderId];

  return (
    <div data-testid="phone-home" className="flex-1 min-h-0 flex flex-col select-none relative">
      <div className="mt-3 mb-3 text-center relative">
        <p className="font-sans font-light text-5xl text-white tracking-tight">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
        <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/70 mt-1">{now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}</p>
        <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/50 mt-1">{me.name || `Citizen #${me.playercode}`}</p>
        <button data-testid="phone-arrange-toggle" onClick={() => { setEdit((v) => !v); setSelected(""); setOpenFolderId(""); }}
          className={`absolute right-1 top-1 font-mono text-[8px] uppercase tracking-[0.15em] border px-2.5 py-1.5 rounded-full transition-colors ${edit ? "border-white text-white bg-white/15" : "border-white/20 text-white/55 hover:text-white"}`}>
          {edit ? "Done" : "Arrange"}
        </button>
        {edit && <p data-testid="phone-arrange-help" className="font-mono text-[7px] uppercase tracking-[0.14em] text-white/40 mt-2">Tap two apps to group · drag to reorder · dots move pages</p>}
      </div>

      <div data-testid="phone-pages" className="flex-1 min-h-0 overflow-hidden" onPointerDown={pointerDown} onPointerUp={pointerUp} style={{ touchAction: "pan-y" }}>
        <div className="h-full flex transition-transform duration-300 ease-out" style={{ transform: `translateX(-${page * 100}%)` }}>
          {localPages.map((ids, pageIndex) => (
            <div key={pageIndex} data-testid={`phone-page-${pageIndex + 1}`} className="w-full shrink-0 px-2 pt-1">
              <div className="grid grid-cols-4 gap-y-6 gap-x-2 content-start" style={{ animation: "phAppIn 0.35s cubic-bezier(0.2, 0.9, 0.3, 1) both" }}>
                {ids.map((item) => item.startsWith("folder:") && localFolders[item.slice(7)] ? (
                  <FolderSlot key={item} folder={localFolders[item.slice(7)]} appsById={byId} edit={edit}
                              selected={selected === item} accent={accent} onPress={pressFolder} onDragStart={setSelected} onDropApp={() => addToFolder(item.slice(7), selected)} />
                ) : byId[item] && (
                  <AppIcon key={item} app={byId[item]} edit={edit} selected={selected === item}
                           onPress={pressIcon} onDragStart={setSelected} onDropApp={(targetId) => selected && createFolder(selected, targetId)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {notice && (
        <div data-testid="phone-layout-notice" className="mx-auto mb-1 max-w-[280px] rounded-full border border-amber-300/35 bg-black/75 px-3 py-1.5 text-center font-mono text-[8px] uppercase tracking-[0.14em] text-amber-200 backdrop-blur-md">
          {notice}
        </div>
      )}

      <div data-testid="phone-page-dots" className="flex items-center justify-center gap-2 py-2">
        {localPages.map((_, i) => (
          <button key={i} data-testid={`phone-page-dot-${i + 1}`} onClick={() => moveToPage(i)}
                  onDragOver={(e) => edit && e.preventDefault()} onDrop={(e) => { e.preventDefault(); moveToPage(i); }}
                  className={`rounded-full transition-all ${page === i ? "w-5 h-2" : "w-2 h-2"} ${edit ? "border border-white/30" : ""}`}
                  style={{ backgroundColor: page === i ? accent : "rgba(255,255,255,0.28)" }}
                  title={edit && selected ? `Move ${selected} to page ${i + 1}` : `Page ${i + 1}`} />
        ))}
      </div>

      <div className="mx-2 mb-2 rounded-3xl bg-white/10 backdrop-blur-md border border-white/10 py-3 flex justify-around">
        {dock.map(({ id, Icon }) => (
          <button key={id} data-testid={`dock-${id}`} onClick={() => byId[id]?.onOpen?.()} className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
            <Icon className="w-5 h-5" style={{ color: accent }} />
          </button>
        ))}
      </div>

      {openFolder && (
        <div data-testid="phone-folder-overlay" className="absolute inset-2 z-30 rounded-3xl border border-white/15 bg-[#070707]/95 backdrop-blur-xl p-4 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            {edit ? (
              <input data-testid="folder-name-input" value={folderName} maxLength={18} onChange={(e) => setFolderName(e.target.value)}
                     onBlur={renameFolder} onKeyDown={(e) => e.key === "Enter" && renameFolder()}
                     className="flex-1 bg-white/10 border border-white/15 rounded-xl px-3 py-2 font-sans font-semibold text-sm text-white focus:outline-none" />
            ) : <p data-testid="folder-title" className="flex-1 font-sans font-semibold text-sm text-white">{openFolder.name}</p>}
            {edit && <Pencil className="w-4 h-4 text-white/40" />}
            <button data-testid="folder-close" onClick={() => { setOpenFolderId(""); setSelected(""); }} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-4 gap-y-6 gap-x-2 content-start flex-1 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
            {openFolder.apps.map((id) => byId[id] && (
              <AppIcon key={id} app={byId[id]} edit={edit} selected={selected === id}
                       onPress={() => folderAppPress(openFolder, id)} onDragStart={setSelected} onDropApp={() => {}} />
            ))}
          </div>
          {edit && (
            <div className="flex gap-2 pt-3 border-t border-white/10">
              <button data-testid="folder-move-out" onClick={moveSelectedOut} disabled={!selected || !openFolder.apps.includes(selected)}
                      className="flex-1 py-2.5 rounded-xl border border-white/15 font-mono text-[9px] uppercase tracking-[0.18em] text-white/70 disabled:opacity-35">
                Move out
              </button>
              <button data-testid="folder-dissolve" onClick={dissolveFolder}
                      className="flex-1 py-2.5 rounded-xl border border-red-400/30 font-mono text-[9px] uppercase tracking-[0.18em] text-red-300 flex items-center justify-center gap-1.5">
                <Trash2 className="w-3.5 h-3.5" /> Dissolve
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
